import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserSidebar, AdminSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/Avatar";
import { Send, Paperclip, Trash2, X, ArrowLeft, MoreVertical, UserX } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const mediaSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

function Attachment({ url, type }) {
  if (!url) return null;
  const src = mediaSrc(url);
  if (type === "video") {
    return <video src={src} controls className="max-w-xs sm:max-w-sm rounded-xl mt-2" />;
  }
  return <img src={src} alt="attachment" className="max-w-xs sm:max-w-sm rounded-xl mt-2" />;
}

function Bubble({ m, me, onDelete }) {
  const isMine = m.sender_id === me.id;
  const canDelete = isMine || me.role === "ADMIN";
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} group`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[85%] sm:max-w-md relative flex items-end gap-1 ${isMine ? "flex-row" : "flex-row-reverse"}`}>
        {canDelete && (
          <button
            onClick={() => { if (window.confirm("Obrisati ovu poruku?")) onDelete(m.id); }}
            className="shrink-0 w-8 h-8 rounded-full bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-400 flex items-center justify-center opacity-70 hover:opacity-100 transition"
            aria-label="Obriši poruku"
            data-testid={`msg-del-${m.id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
        <div className={`p-3 rounded-2xl ${isMine ? "bg-[#FFD60A] text-black rounded-br-md" : "bg-white/10 text-white rounded-bl-md"}`}>
          {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
          <Attachment url={m.attachment_url} type={m.attachment_type} />
          <div className={`text-[10px] mt-1 ${isMine ? "text-black/60" : "text-white/40"}`}>{new Date(m.created_at).toLocaleString("hr-HR")}</div>
        </div>
      </div>
    </div>
  );
}

function Composer({ onSend }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const pickFile = () => fileRef.current?.click();
  const onPick = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const clearFile = () => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async () => {
    if (!text.trim() && !file) return;
    setUploading(true);
    try {
      let attachment = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/messages/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment = data;
      }
      await onSend({ body: text.trim(), attachment_url: attachment?.url, attachment_type: attachment?.type });
      setText(""); clearFile();
    } catch (e) { toast.error(formatError(e)); }
    setUploading(false);
  };

  return (
    <div className="border-t border-white/10 p-3 sm:p-4 bg-[#0a0a0a]">
      {preview && (
        <div className="mb-2 relative inline-block">
          {file?.type?.startsWith("video/") ? (
            <video src={preview} className="h-24 rounded-lg" />
          ) : (
            <img src={preview} alt="preview" className="h-24 rounded-lg" />
          )}
          <button onClick={clearFile} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={12}/></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm,image/gif" onChange={onPick} className="hidden" data-testid="msg-file"/>
        <Button size="icon" variant="ghost" onClick={pickFile} className="text-white/70 hover:bg-white/10 rounded-full" data-testid="msg-attach"><Paperclip size={18}/></Button>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Napišite poruku…" rows={1} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} className="flex-1 bg-white/5 border-white/10 text-white rounded-2xl resize-none min-h-[44px] max-h-32" data-testid="msg-input"/>
        <Button className="btn-yellow rounded-full h-11 w-11 p-0" onClick={submit} disabled={uploading} data-testid="msg-send"><Send size={16}/></Button>
      </div>
    </div>
  );
}

export function UserMessages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [requests, setRequests] = useState([]);
  const [people, setPeople] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [open, setOpen] = useState(null); // {id, first_name, last_name, username, avatar_url, relation, isSupport}
  const [msgs, setMsgs] = useState([]);
  const [adminId, setAdminId] = useState(null);
  const [adminName, setAdminName] = useState("Podrška");
  const [search, setSearch] = useState("");
  const scrollRef = useRef();
  const nav = useNavigate();

  const loadLists = async () => {
    try {
      const [{ data: t }, { data: r }, { data: a }] = await Promise.all([
        api.get("/chat/threads"), api.get("/chat/requests"), api.get("/messages/admin-thread"),
      ]);
      setThreads(t.filter((x) => x.user.role !== "ADMIN"));
      setRequests(r);
      setAdminId(a.admin_id); setAdminName(a.admin_name || "Podrška");
    } catch (e) { toast.error(formatError(e)); }
  };
  const loadPeople = async () => {
    try { const { data } = await api.get("/chat/users"); setPeople(data); } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { loadLists(); const i = setInterval(loadLists, 6000); return () => clearInterval(i); }, []);

  const loadThread = async () => {
    if (!open) return;
    try {
      if (open.isSupport) { const { data } = await api.get("/messages/admin-thread"); setMsgs(data.messages || []); }
      else { const { data } = await api.get(`/messages/thread/${open.id}`); setMsgs(data); }
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { setMsgs([]); loadThread(); const t = setInterval(loadThread, 5000); return () => clearInterval(t); }, [open?.id, open?.isSupport]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs]);

  const send = async (payload) => {
    const rid = open?.isSupport ? adminId : open?.id;
    await api.post("/messages", { ...payload, recipient_id: rid });
    loadThread(); loadLists();
  };
  const del = async (id) => { try { await api.delete(`/messages/${id}`); loadThread(); toast.success("Poruka obrisana"); } catch (e) { toast.error(formatError(e)); } };
  const deleteConversation = async () => {
    const rid = open?.isSupport ? adminId : open?.id;
    if (!rid || !window.confirm("Obrisati cijeli razgovor?")) return;
    try { await api.delete(`/messages/conversation/${rid}`); setMsgs([]); toast.success("Razgovor obrisan"); loadLists(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const act = async (rid, action) => {
    try { await api.post(`/chat/requests/${rid}/${action}`); toast.success(action === "accept" ? "Zahtjev prihvaćen" : "Zahtjev odbijen"); loadLists(); if (action === "decline") setOpen(null); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (!user) return null;

  const filtered = people.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${p.first_name} ${p.last_name} ${p.username}`.toLowerCase().includes(q);
  });
  const blocked = open && !open.isSupport && open.relation === "DECLINED_BY_THEM";

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen">
      <UserSidebar />
      <main className="flex-1 flex flex-col lg:flex-row h-[calc(100dvh-56px)] lg:h-screen">
        <div className={`lg:w-80 border-r border-white/10 bg-[#0a0a0a] ${open ? "hidden lg:flex" : "flex"} flex-col`}>
          <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-heading font-bold text-white">Poruke</h2>
            <Button size="sm" className="btn-yellow rounded-full h-8 px-3" onClick={() => { setShowPicker((v) => !v); if (!people.length) loadPeople(); }} data-testid="new-chat-btn">Novi</Button>
          </div>

          {showPicker && (
            <div className="border-b border-white/10">
              <div className="p-2">
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Traži korisnika…" className="bg-white/5 border-white/10 text-white h-9" data-testid="chat-user-search"/>
              </div>
              <div className="max-h-64 overflow-y-auto stream-scroll">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    disabled={p.relation === "DECLINED_BY_THEM"}
                    onClick={() => { setOpen({ ...p }); setShowPicker(false); }}
                    className="w-full text-left p-3 hover:bg-white/5 flex items-center gap-3 border-b border-white/5 disabled:opacity-40"
                    data-testid={`pick-person-${p.id}`}
                  >
                    <Avatar user={p} size={32}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{p.first_name} {p.last_name}</div>
                      <div className="text-white/40 text-xs truncate">
                        @{p.username}
                        {p.relation === "PENDING_OUT" && " · zahtjev poslan"}
                        {p.relation === "PENDING_IN" && " · želi razgovor"}
                        {p.relation === "DECLINED_BY_THEM" && " · ne prihvaća poruke"}
                      </div>
                    </div>
                  </button>
                ))}
                {filtered.length === 0 && <div className="p-3 text-white/40 text-sm">Nema korisnika</div>}
              </div>
            </div>
          )}

          {requests.length > 0 && (
            <div className="border-b border-white/10" data-testid="chat-requests">
              <div className="px-4 py-2 text-white/50 text-xs uppercase tracking-widest">Zahtjevi za razgovor</div>
              {requests.map((r) => (
                <div key={r.id} className="p-3 border-b border-white/5" data-testid={`chat-request-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Avatar user={r.user} size={32}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">{r.user.first_name} {r.user.last_name}</div>
                      <div className="text-white/40 text-xs truncate">{r.last_message?.body || "Želi vam poslati poruku"}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="btn-yellow rounded-full h-8 px-3 flex-1" onClick={() => act(r.id, "accept")} data-testid={`accept-${r.id}`}>Prihvati</Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 rounded-full h-8 px-3 flex-1" onClick={() => act(r.id, "decline")} data-testid={`decline-${r.id}`}>Odbij</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-y-auto stream-scroll">
            <button onClick={() => setOpen({ id: adminId, isSupport: true, first_name: adminName, last_name: "" })} className={`w-full text-left p-3 flex items-center gap-3 border-b border-white/5 ${open?.isSupport ? "bg-white/10" : "hover:bg-white/5"}`} data-testid="thread-support">
              <div className="w-10 h-10 rounded-full bg-[#FFD60A] flex items-center justify-center text-black font-heading font-black">{(adminName || "S")[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{adminName}</div>
                <div className="text-white/40 text-xs">Podrška</div>
              </div>
            </button>
            {threads.map((t) => (
              <button key={t.user.id} onClick={() => setOpen({ ...t.user, relation: t.relation })} className={`w-full text-left p-3 flex items-center gap-3 border-b border-white/5 ${open?.id === t.user.id && !open?.isSupport ? "bg-white/10" : "hover:bg-white/5"}`} data-testid={`thread-${t.user.id}`}>
                <Avatar user={t.user} size={40}/>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{t.user.first_name} {t.user.last_name}</div>
                  <div className="text-white/40 text-xs truncate">{t.last_message?.body || (t.last_message?.attachment_type ? `[${t.last_message.attachment_type}]` : "")}</div>
                </div>
                {t.unread > 0 && <span className="bg-[#FFD60A] text-black text-xs rounded-full px-2 py-0.5 font-bold">{t.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className={`flex-1 flex-col ${open ? "flex" : "hidden lg:flex"}`}>
          {open ? (
            <>
              <div className="border-b border-white/10 px-3 sm:px-6 py-3 sm:py-4 flex items-center gap-3 bg-[#0a0a0a]">
                <button onClick={() => setOpen(null)} className="lg:hidden text-white p-2 -ml-2 rounded-full hover:bg-white/10" aria-label="Nazad" data-testid="msg-back-user"><ArrowLeft size={20}/></button>
                <button onClick={() => nav("/dashboard")} className="hidden lg:flex text-white p-2 -ml-2 rounded-full hover:bg-white/10" aria-label="Zatvori" data-testid="msg-close-user"><X size={20}/></button>
                {open.isSupport
                  ? <div className="w-10 h-10 rounded-full bg-[#FFD60A] flex items-center justify-center text-black font-heading font-black" data-testid="support-avatar">{(adminName || "S")[0]}</div>
                  : <Avatar user={open} size={40}/>}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-heading font-bold truncate" data-testid="chat-title">{open.first_name} {open.last_name}</div>
                  <div className="text-white/50 text-xs truncate">
                    {open.isSupport ? "Podrška" : open.relation === "PENDING_OUT" ? "Čeka prihvaćanje zahtjeva" : open.relation === "DECLINED_BY_THEM" ? "Ne prihvaća vaše poruke" : `@${open.username || ""}`}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-white p-2 rounded-full hover:bg-white/10" aria-label="Više opcija" data-testid="msg-user-menu"><MoreVertical size={20}/></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#171717] border-white/10 text-white">
                    <DropdownMenuItem onClick={deleteConversation} className="text-red-400 focus:bg-red-500/10 focus:text-red-300" data-testid="user-del-conversation">
                      <Trash2 size={14} className="mr-2"/>Obriši razgovor
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 stream-scroll">
                {msgs.length === 0 && <div className="text-white/50 text-center mt-10">{open.isSupport ? "Pošaljite prvu poruku administratoru." : "Pošaljite prvu poruku — korisnik je mora prihvatiti."}</div>}
                {msgs.map((m) => <Bubble key={m.id} m={m} me={user} onDelete={del} />)}
              </div>
              {blocked
                ? <div className="border-t border-white/10 p-4 text-center text-white/50 text-sm" data-testid="chat-blocked">Ovaj korisnik je odbio poruke, ne možete mu više pisati.</div>
                : <Composer onSend={send} />}
            </>
          ) : (
            <div className="flex-1 hidden lg:flex items-center justify-center text-white/40">Odaberite razgovor</div>
          )}
        </div>
      </main>
    </div>
  );
}

export function AdminMessages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [users, setUsers] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const scrollRef = useRef();

  const loadThreads = async () => { try { const [{ data: t }, { data: u }] = await Promise.all([api.get("/admin/messages/threads"), api.get("/admin/users")]); setThreads(t); setUsers(u); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { loadThreads(); const i = setInterval(loadThreads, 5000); return () => clearInterval(i); }, []);

  const loadThread = async (otherId) => {
    try { const { data } = await api.get(`/messages/thread/${otherId}`); setMsgs(data); }
    catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { if (openThread) { loadThread(openThread.id); const t = setInterval(() => loadThread(openThread.id), 5000); return () => clearInterval(t); } }, [openThread?.id]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs]);

  const send = async (payload) => {
    if (!openThread) return;
    await api.post("/messages", { ...payload, recipient_id: openThread.id });
    loadThread(openThread.id); loadThreads();
  };
  const del = async (id) => { try { await api.delete(`/messages/${id}`); loadThread(openThread.id); toast.success("Poruka obrisana"); } catch (e) { toast.error(formatError(e)); } };

  const deleteConversation = async () => {
    if (!openThread) return;
    if (!window.confirm(`Obrisati sve poruke s korisnikom ${openThread.first_name} ${openThread.last_name}?`)) return;
    try { await api.delete(`/messages/conversation/${openThread.id}`); toast.success("Razgovor obrisan"); setMsgs([]); setOpenThread(null); loadThreads(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteUserAccount = async () => {
    if (!openThread) return;
    if (!window.confirm(`TRAJNO obrisati korisnika ${openThread.email}? Ovo briše korisnika, sve njegove poruke, uplate i zahtjeve.`)) return;
    try { await api.delete(`/admin/users/${openThread.id}`); toast.success("Korisnik trajno obrisan"); setOpenThread(null); loadThreads(); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (!user) return null;

  const otherUsers = users.filter((u) => !threads.find((t) => t.user.id === u.id));

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen">
      <AdminSidebar />
      <main className="flex-1 flex flex-col lg:flex-row h-[calc(100dvh-56px)] lg:h-screen">
        {/* Threads list */}
        <div className={`lg:w-80 border-r border-white/10 bg-[#0a0a0a] ${openThread ? "hidden lg:flex" : "flex"} flex-col`}>
          <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-heading font-bold text-white">Razgovori</h2>
            <Button size="sm" className="btn-yellow rounded-full h-8 px-3" onClick={() => setShowUserPicker((v) => !v)} data-testid="new-thread">Novi</Button>
          </div>
          {showUserPicker && (
            <div className="border-b border-white/10 max-h-64 overflow-y-auto">
              {otherUsers.map((u) => (
                <button key={u.id} onClick={() => { setOpenThread(u); setShowUserPicker(false); }} className="w-full text-left p-3 hover:bg-white/5 flex items-center gap-3 border-b border-white/5" data-testid={`pick-user-${u.id}`}>
                  <Avatar user={u} size={32}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{u.first_name} {u.last_name}</div>
                    <div className="text-white/40 text-xs truncate">{u.email}</div>
                  </div>
                </button>
              ))}
              {otherUsers.length === 0 && <div className="p-3 text-white/40 text-sm">Nema drugih korisnika</div>}
            </div>
          )}
          <div className="flex-1 overflow-y-auto stream-scroll">
            {threads.length === 0 && !showUserPicker && <div className="p-4 text-white/40 text-sm">Nema razgovora. Klik "Novi" da pošaljete poruku korisniku.</div>}
            {threads.map((t) => (
              <button key={t.user.id} onClick={() => setOpenThread(t.user)} className={`w-full text-left p-3 flex items-center gap-3 border-b border-white/5 ${openThread?.id === t.user.id ? "bg-white/10" : "hover:bg-white/5"}`} data-testid={`thread-${t.user.id}`}>
                <Avatar user={t.user} size={40}/>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate">{t.user.first_name} {t.user.last_name}</div>
                  <div className="text-white/40 text-xs truncate">{t.last_message?.body || (t.last_message?.attachment_type ? `[${t.last_message.attachment_type}]` : "")}</div>
                </div>
                {t.unread > 0 && <span className="bg-[#FFD60A] text-black text-xs rounded-full px-2 py-0.5 font-bold">{t.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Chat pane */}
        <div className={`flex-1 flex-col ${openThread ? "flex" : "hidden lg:flex"}`}>
          {openThread ? (
            <>
              <div className="border-b border-white/10 px-3 sm:px-6 py-3 sm:py-4 flex items-center gap-3 bg-[#0a0a0a]">
                <button onClick={() => setOpenThread(null)} className="lg:hidden text-white p-2 -ml-2 rounded-full hover:bg-white/10" aria-label="Nazad na razgovore" data-testid="back-threads"><ArrowLeft size={20}/></button>
                <button onClick={() => nav("/admin/dashboard")} className="hidden lg:flex text-white p-2 -ml-2 rounded-full hover:bg-white/10" aria-label="Zatvori" data-testid="msg-close-admin"><X size={20}/></button>
                <Avatar user={openThread} size={40}/>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-heading font-bold truncate">{openThread.first_name} {openThread.last_name}</div>
                  <div className="text-white/50 text-xs truncate">{openThread.email}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-white p-2 rounded-full hover:bg-white/10" aria-label="Više opcija" data-testid="msg-admin-menu"><MoreVertical size={20}/></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#171717] border-white/10 text-white">
                    <DropdownMenuItem onClick={deleteConversation} className="text-yellow-400 focus:bg-yellow-500/10 focus:text-yellow-300" data-testid="admin-del-conversation">
                      <Trash2 size={14} className="mr-2"/>Obriši razgovor
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10"/>
                    <DropdownMenuItem onClick={deleteUserAccount} className="text-red-400 focus:bg-red-500/10 focus:text-red-300" data-testid="admin-del-user">
                      <UserX size={14} className="mr-2"/>Obriši korisnika trajno
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 stream-scroll">
                {msgs.length === 0 && <div className="text-white/50 text-center mt-10">Nema poruka. Pošaljite prvu.</div>}
                {msgs.map((m) => <Bubble key={m.id} m={m} me={user} onDelete={del} />)}
              </div>
              <Composer onSend={send} />
            </>
          ) : (
            <div className="flex-1 hidden lg:flex items-center justify-center text-white/40">Odaberite razgovor</div>
          )}
        </div>
      </main>
    </div>
  );
}
